import axios from 'axios';

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const BASE_URL = process.env.REACT_APP_REPORTS_BASE_URL;

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };


export async function refreshToken(currentToken) {
  const url = `${BASE_URL}/auth/rf-token`;
  const headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    'Idempotency-Key': uuid4(),
  };
  const response = await axios.get(url, { headers });
  return response.data.token;
}

/**
 * Registered `game-server/report/*` calls use `Authorization: Bearer <token>`.
 * Org users often have no `user.token` (Firebase) but always have session composite
 * `sessionId&organizationId` from login → same shape as `adminToken` / health-sprint-admin middleware.
 */
export function resolveReportsAuthToken(userToken, adminToken) {
  if (userToken != null && String(userToken).trim() !== "") return String(userToken).trim();
  if (adminToken != null && String(adminToken).trim() !== "") return String(adminToken).trim();
  return null;
}

/** Guest report API expects `time` as a string (e.g. 00:00:53), not raw seconds — Prisma String fields reject numbers. */
function guestTimeStringForApi(timeRaw) {
  const sec = normalizeReportTimeValue(timeRaw);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Only true guests use `game/add|update/guest/score` (same as who_wants_to_be).
 * ORG reports are created/updated via `game-server/report/*` + Bearer; guest PUT does not update those rows.
 */
function usesGuestReportEndpoints(data) {
  if (!data || typeof data !== "object") return false;
  const r = String(data.role ?? "").trim().toUpperCase();
  const ut = String(data.user_type ?? "").trim().toUpperCase();
  return r === "GUEST_USER" || ut === "GUEST_USER";
}

export async function sendReport(data) {
  const { sessionId, role, token } = data;
  if (role === "demobypass") return true;

  /** Skip guest routes (e.g. login seed for ORG when `game/add/guest/score` returns no id). */
  const forceRegistered = data.useRegisteredReportApi === true;

  if (!forceRegistered && usesGuestReportEndpoints(data)) {
    const timeStr = guestTimeStringForApi(data.time);
    const endpoint = data.reportId ? 'game/update/guest/score' : 'game/add/guest/score';
    const guestPayload = data.reportId
      ? { points: data.points, time: timeStr, reportId: data.reportId }
      : (() => {
          /** Login seed uses who_wants_to_be shape — some report APIs reject or skip `user_type` / `email` on guest add. */
          if (data.guestScoreMinimal === true) {
            return {
              points: data.points,
              time: timeStr,
              gameId: data.gameId,
              name: data.name,
              sessionId: data.sessionId,
              userId: data.userId,
              organizationId: data.organizationId,
              ans:
                data.ans != null && String(data.ans).trim() !== ''
                  ? String(data.ans)
                  : '',
            };
          }
          const p = {
            points: data.points,
            time: timeStr,
            gameId: data.gameId,
            name: data.name,
            sessionId: data.sessionId,
            userId: data.userId,
            organizationId: data.organizationId,
            ans: data.ans != null && String(data.ans).trim() !== '' ? String(data.ans) : '',
          };
          if (data.email != null && String(data.email).trim() !== '') {
            p.email = String(data.email).trim();
          }
          const ut = data.user_type || data.role;
          if (ut != null && String(ut).trim() !== '') {
            p.user_type = String(ut).trim();
          }
          return p;
        })();
    const url = `${BASE_URL}/${endpoint}`;
    const method = data.reportId ? 'put' : 'post';
    const headers = {
      'client-secret': CLIENT_SECRET,
      'Content-Type': 'application/json',
      'Idempotency-Key': uuid4(),
    };
    const response = await axios({ method, url, headers, data: guestPayload });
    return response.data;
  }

  // Registered user logic
  const {
    useRegisteredReportApi: _omitReg,
    guestScoreMinimal: _omitGuestMin,
    ...reportBody
  } = data;
  const ridForUrl =
    reportBody.reportId != null && String(reportBody.reportId).trim() !== ''
      ? String(reportBody.reportId).trim()
      : '';
  const isUpdate = ridForUrl !== '';
  const url = isUpdate
    ? `${BASE_URL}/game-server/report/${ridForUrl}`
    : `${BASE_URL}/game-server/report/add`;
  const method = isUpdate ? 'put' : 'post';

  const { token: _omitTokenInBody, ...bodyWithoutToken } = reportBody;
  const registeredData = { ...bodyWithoutToken, time: guestTimeStringForApi(reportBody.time) };
  if (!isUpdate) {
    delete registeredData.reportId;
  } else {
    registeredData.reportId = ridForUrl;
  }

  let headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'Idempotency-Key': uuid4(),
  };

  try {
    const response = await axios({ method, url, headers, data: registeredData });
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      const newToken = await refreshToken(token);
      headers.Authorization = `Bearer ${newToken}`;
      const retry = await axios({ method, url, headers, data: registeredData });
      return retry.data;
    }
    throw err;
  }
}

/** Seconds for report API: number, or "MM:SS" / "HH:MM:SS" from DB. */
export function normalizeReportTimeValue(raw) {
  if (raw == null || raw === "") return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));
  const s = String(raw).trim();
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function isUuidLikeString(s, userId) {
  const t = String(s).trim();
  const uid = userId != null ? String(userId).trim() : "";
  return t.length >= 32 && /^[0-9a-f-]{36}$/i.test(t) && (uid === "" || t !== uid);
}

/** Nested `{ report: { id } }` / Prisma rows: `id` + score/session fields — not plain `organization.id`. */
function idFromReportShapedObject(node, userId) {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;
  const idRaw = node.id;
  if (idRaw == null) return null;
  const sid = String(idRaw).trim();
  if (!isUuidLikeString(sid, userId)) return null;
  const hasReportShape =
    node.gameId != null ||
    node.sessionId != null ||
    node.points != null ||
    node.time != null ||
    node.reportId != null ||
    (node.userId != null && node.organizationId != null);
  if (!hasReportShape) return null;
  return sid;
}

/** Reports API shapes vary; only follow nested objects + report-* keys (avoid grabbing organizationId UUIDs). */
function deepExtractReportId(res, userId) {
  const uid = userId != null ? String(userId).trim() : "";
  const ws = new WeakSet();
  function isUuidLike(s) {
    return isUuidLikeString(s, userId);
  }
  function walk(node, depth, parentKey) {
    if (depth > 14 || node == null || typeof node !== "object") return null;
    if (ws.has(node)) return null;
    ws.add(node);
    const priorityKeys = ["reportId", "report_id", "gameReportId", "game_report_id", "reportUUID"];
    if (!Array.isArray(node)) {
      const rowId = idFromReportShapedObject(node, userId);
      if (rowId) return rowId;
      const pk = parentKey != null ? String(parentKey) : "";
      if (
        /report/i.test(pk) &&
        node.id != null &&
        isUuidLikeString(node.id, userId)
      ) {
        return String(node.id).trim();
      }
      for (const k of priorityKeys) {
        if (Object.prototype.hasOwnProperty.call(node, k) && node[k] != null) {
          const s = String(node[k]).trim();
          if (s && (uid === "" || s !== uid)) return s;
        }
      }
      for (const [k, v] of Object.entries(node)) {
        if (v != null && typeof v === "object") {
          const x = walk(v, depth + 1, k);
          if (x) return x;
        }
        if (typeof v === "string" && /report/i.test(k) && isUuidLike(v)) {
          return String(v).trim();
        }
      }
    } else {
      for (const item of node) {
        const x = walk(item, depth + 1, parentKey);
        if (x) return x;
      }
    }
    return null;
  }
  return walk(res, 0, null);
}

function firstDistinctReportId(candidates, userId) {
  const uid = userId != null ? String(userId).trim() : "";
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s === "") continue;
    if (uid !== "" && s === uid) continue;
    return s;
  }
  return null;
}

/** `{ id: uuid, message: "..." }` and similar thin success bodies without game/session fields. */
function extractIdFromApiSuccessWrapper(res, userId) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return null;
  if (res.error != null || res.errors != null) return null;
  const idRaw = res.id;
  if (idRaw == null) return null;
  const s = String(idRaw).trim();
  if (!isUuidLikeString(s, userId)) return null;
  const keys = Object.keys(res);
  if (keys.length > 24) return null;
  const softSignal =
    res.success === true ||
    res.ok === true ||
    res.status === "success" ||
    res.status === 200 ||
    res.statusCode === 200 ||
    (typeof res.message === "string" && res.message.length > 0) ||
    (typeof res.msg === "string" && res.msg.length > 0) ||
    res.createdAt != null ||
    res.updatedAt != null;
  if (softSignal || keys.length <= 10) return s;
  return null;
}

export function extractReportIdFromResponse(res, userId, _depth = 0) {
  if (res == null || res === true) return null;
  if (_depth > 4) return null;
  if (typeof res === "string" || typeof res === "number") {
    return firstDistinctReportId([res], userId);
  }
  if (typeof res !== "object") return null;

  if (typeof res.data === "string" && res.data.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(res.data);
      const nested = extractReportIdFromResponse(parsed, userId, _depth + 1);
      if (nested) return nested;
    } catch {
      /* ignore */
    }
  }

  const rowId = idFromReportShapedObject(res, userId);
  if (rowId) return rowId;

  const thin = extractIdFromApiSuccessWrapper(res, userId);
  if (thin) return thin;

  const row0 =
    Array.isArray(res.rows) && res.rows[0] != null
      ? idFromReportShapedObject(res.rows[0], userId)
      : null;
  if (row0) return row0;

  const candidates = [
    res.reportId,
    res.report_id,
    res.reportid,
    res.gameReportId,
    res.game_report_id,
    res.reportUUID,
    res.uuid,
    res.insertId != null ? String(res.insertId) : null,
    res.report?.id,
    res.report?.reportId,
    res.report?.report_id,
    res.userReport?.id,
    res.userReport?.reportId,
    res.gameReport?.id,
    res.gameReport?.reportId,
    res.data?.reportId,
    res.data?.report_id,
    res.data?.id,
    res.data?.uuid,
    res.data?.report?.id,
    res.data?.report?.reportId,
    res.data?.data?.reportId,
    res.data?.data?.id,
    res.result?.reportId,
    res.result?.id,
    res.result?.report?.id,
    res.result?.data?.reportId,
    res.payload?.reportId,
    res.payload?.id,
    res.payload?.report?.id,
    res.created?.id,
    res.created?.reportId,
    res.entity?.id,
    res.game?.reportId,
    Array.isArray(res.rows) ? res.rows[0]?.id : null,
    res.record?.id,
    res.item?.id,
    res.items?.[0]?.id,
    res.items?.[0]?.reportId,
  ];
  const direct = firstDistinctReportId(candidates, userId);
  if (direct) return direct;

  if (res.data != null && typeof res.data === "object" && _depth < 4) {
    const fromData = extractReportIdFromResponse(res.data, userId, _depth + 1);
    if (fromData) return fromData;
  }

  return deepExtractReportId(res, userId);
}

/** Non-empty report id string for PUT; empty if first POST. */
export function resolveReportIdForSendReport({ reportId }) {
  if (reportId == null) return "";
  const s = String(reportId).trim();
  return s;
}